// B2B desk model: the 8-step journey, the Today queues and the per-lead next step.
// Pure functions only, so the views stay thin and this file is unit-tested.

export const JOURNEY = [
  { key: 'new', label: 'New', stages: ['identified'] },
  { key: 'contacted', label: 'Contacted', stages: ['contacted', 'winback'] },
  { key: 'interested', label: 'Interested', stages: ['replied'] },
  { key: 'swatch', label: 'Swatch', stages: ['swatch_requested', 'swatch_sent'] },
  { key: 'delivered', label: 'Delivered', stages: ['swatch_delivered'] },
  { key: 'sample', label: 'Sample order', stages: ['sampling'] },
  { key: 'quote', label: 'Quote', stages: ['quote_sent'] },
  { key: 'won', label: 'Won', stages: ['bulk_client'] },
];

// Next step per stage: what the owner does, which tool opens, and the button label.
export const STAGES = {
  identified: { label: 'New', next: 'Check fit, assign an owner, send the first email', cta: 'Send first email', tool: 'email' },
  contacted: { label: 'Contacted', next: 'Send the next touch', cta: 'Send follow-up', tool: 'email' },
  winback: { label: 'Win-back', next: 'Send the win-back email', cta: 'Send win-back', tool: 'email' },
  replied: { label: 'Interested', next: 'Reply and ask fabric, quantity and city', cta: 'Reply', tool: 'reply' },
  swatch_requested: { label: 'Swatch to pack', next: 'Pack the niche kit and enter the AWB', cta: 'Pack swatch', tool: 'swatch' },
  swatch_sent: { label: 'Swatch on the way', next: 'Check tracking, mark delivered', cta: 'Update tracking', tool: 'swatch' },
  swatch_delivered: { label: 'Delivered', next: 'Call: did the drape and print work?', cta: 'Log call', tool: 'call' },
  sampling: { label: 'Sample order', next: 'Get approval on colour and hand-feel', cta: 'Record approval', tool: 'sample' },
  quote_sent: { label: 'Quote', next: 'Chase the quote until yes or no', cta: 'Chase quote', tool: 'quote' },
  bulk_client: { label: 'Won', next: 'Reorder reminder and new prints', cta: 'Send new prints', tool: 'email' },
  lost: { label: 'Lost', next: '', cta: '', tool: '' },
};

export const STAGE_ORDER = ['identified', 'contacted', 'winback', 'replied', 'swatch_requested', 'swatch_sent', 'swatch_delivered', 'sampling', 'quote_sent', 'bulk_client', 'lost'];

export const LOST_REASONS = {
  not_interested: 'Not interested',
  no_response: 'No reply after 5 touches',
  price: 'Price too high',
  quality: 'Quality or hand-feel not right',
  wrong_fit: 'Not our buyer profile',
  unsubscribed: 'Asked not to be contacted',
  other: 'Other',
};

export const KEEP_OUT_REASONS = {
  not_b2b: 'Retail buyer, not B2B',
  competitor: 'Competitor',
  internal: 'Staff, friend or supplier',
  wrong_fit: 'Not our buyer profile',
  duplicate: 'Duplicate record',
  test: 'Test or junk',
  other: 'Other',
};

export const SEGMENTS = {
  lapsed: { label: 'Lapsed 120d+', hint: '2+ orders, quiet for 120+ days' },
  cooling: { label: 'Cooling', hint: '2+ orders, quiet 46 to 120 days' },
  one_time: { label: 'One-time', hint: 'Ordered once, never came back' },
  active: { label: 'Active', hint: 'Ordered in the last 45 days' },
  pending_approval: { label: 'Pending approval', hint: 'Support still has to approve' },
  never_ordered: { label: 'Never ordered', hint: 'Approved but no order yet' },
};

export const WINBACK_PER_DAY = 10;

export function journeyIndex(stage) {
  return Math.max(0, JOURNEY.findIndex((j) => j.stages.includes(stage)));
}

export function istToday(now = new Date()) {
  return now.toLocaleDateString('sv-SE', { timeZone: 'Asia/Kolkata' });
}

const dayMs = 86400000;
export function daysBetween(fromIso, toIso) {
  return Math.round((new Date(`${toIso}T00:00:00Z`) - new Date(`${fromIso}T00:00:00Z`)) / dayMs);
}

export const isOpen = (lead) => lead.stage !== 'lost';
export const isSnoozed = (lead, today) => Boolean(lead.snoozedUntil) && lead.snoozedUntil > today;

export function dueLabel(lead, today) {
  if (!lead.followUpDate) return { text: 'no date', tone: 'muted' };
  const d = daysBetween(today, lead.followUpDate);
  if (d < 0) return { text: `${-d} day${d === -1 ? '' : 's'} late`, tone: 'late' };
  if (d === 0) return { text: 'due today', tone: 'soon' };
  if (d === 1) return { text: 'tomorrow', tone: 'muted' };
  if (d < 7) return { text: `in ${d} days`, tone: 'muted' };
  return { text: new Date(`${lead.followUpDate}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }), tone: 'muted' };
}

const spend = (lead) => Number(lead.account?.totalSpent) || 0;

function ownerMatch(lead, ownerFilter) {
  if (!ownerFilter || ownerFilter === 'all') return true;
  if (ownerFilter === 'unassigned') return !lead.owner;
  return lead.owner === ownerFilter;
}

/**
 * Today queues in the order work should happen. Each open, awake lead appears in at most one group,
 * so a busy lead is never counted twice. `me` lets an admin see team leads that are 2+ days late.
 */
export function buildToday(allLeads, { today, ownerFilter = 'all', me = null } = {}) {
  const leads = allLeads.filter((l) => !l.keptOut);
  const awake = leads.filter((l) => isOpen(l) && !isSnoozed(l, today));
  const mine = awake.filter((l) => ownerMatch(l, ownerFilter));
  const taken = new Set();
  const take = (list) => list.filter((l) => !taken.has(l.id)).map((l) => { taken.add(l.id); return l; });
  const due = (l) => l.followUpDate && l.followUpDate <= today;

  const groups = [
    { key: 'replied', title: 'Buyer replied', hint: 'answer within 4 hours', tone: 'action',
      items: take(mine.filter((l) => l.stage === 'replied').sort((a, b) => String(b.lastReplyAt || '').localeCompare(String(a.lastReplyAt || '')))) },
    { key: 'delivered', title: 'Swatch delivered, call now', hint: 'the moment that wins or loses the sample', tone: 'action',
      items: take(mine.filter((l) => l.stage === 'swatch_delivered')) },
    { key: 'pack', title: 'Swatch to pack', hint: 'dispatch within 24 hours', tone: 'info',
      items: take(mine.filter((l) => l.stage === 'swatch_requested')) },
    { key: 'overdue', title: 'Overdue', hint: 'your owner sees these after 2 days', tone: 'late',
      items: take(mine.filter((l) => l.followUpDate && l.followUpDate < today && l.stage !== 'winback').sort((a, b) => a.followUpDate.localeCompare(b.followUpDate))) },
    { key: 'dueToday', title: 'Due today', hint: 'follow-ups, sample checks and quote chases', tone: 'info',
      items: take(mine.filter((l) => due(l) && l.stage !== 'winback' && l.stage !== 'identified')) },
    { key: 'winback', title: 'Win-back today', hint: `${WINBACK_PER_DAY} biggest past spenders not yet contacted`, tone: 'won',
      items: take(mine.filter((l) => l.stage === 'winback' && !Number(l.touchCount)).sort((a, b) => spend(b) - spend(a)).slice(0, WINBACK_PER_DAY)) },
    { key: 'noDate', title: 'No next step', hint: 'every open lead needs a date: set one, snooze or close', tone: 'late',
      items: take(mine.filter((l) => !l.followUpDate && l.owner && !['identified', 'winback', 'bulk_client'].includes(l.stage))) },
  ];

  if (me?.isAdmin && ownerFilter === me.email) {
    const escalated = awake.filter((l) => l.owner && l.owner !== me.email && l.followUpDate && daysBetween(l.followUpDate, today) >= 2);
    groups.splice(3, 0, { key: 'escalated', title: 'Team leads 2+ days late', hint: 'nudge the owner or take it over', tone: 'late', items: escalated.filter((l) => !taken.has(l.id)) });
  }

  const unassigned = awake.filter((l) => !l.owner);
  const counts = {
    replied: groups.find((g) => g.key === 'replied').items.length,
    overdue: mine.filter((l) => l.followUpDate && l.followUpDate < today).length,
    dueToday: mine.filter((l) => l.followUpDate === today).length,
    unassigned: unassigned.length,
    pack: mine.filter((l) => l.stage === 'swatch_requested').length,
    delivered: mine.filter((l) => l.stage === 'swatch_delivered').length,
    quotes: mine.filter((l) => l.stage === 'quote_sent').length,
    winback: mine.filter((l) => l.stage === 'winback' && !Number(l.touchCount)).length,
    snoozed: leads.filter((l) => isOpen(l) && isSnoozed(l, today) && ownerMatch(l, ownerFilter)).length,
    lost: leads.filter((l) => !isOpen(l) && ownerMatch(l, ownerFilter)).length,
  };
  const visible = groups.filter((g) => g.items.length);
  return { groups: visible, counts, total: visible.reduce((n, g) => n + g.items.length, 0) };
}

export function nextStep(lead, today) {
  const info = STAGES[lead.stage] || STAGES.identified;
  if (!isOpen(lead)) return { text: LOST_REASONS[lead.lostReason] ? `Lost: ${LOST_REASONS[lead.lostReason]}` : 'Lost', cta: '', tool: '', due: null };
  if (isSnoozed(lead, today)) return { text: `Snoozed until ${lead.snoozedUntil}`, cta: 'Wake now', tool: 'wake', due: null };
  let text = info.next;
  if (lead.stage === 'contacted' && Number(lead.touchCount)) text = `Send email ${Number(lead.touchCount) + 1} of 5`;
  if (lead.stage === 'replied' && lead.lastReplySnippet) text = `Reply: "${lead.lastReplySnippet.slice(0, 90)}"`;
  if (!lead.owner) text = `Assign an owner, then: ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  return { text, cta: info.cta, tool: info.tool, due: dueLabel(lead, today) };
}

export function whatsappLink(lead, text) {
  const digits = String(lead.phone || '').replace(/\D/g, '');
  if (digits.length < 9) return '';
  const number = digits.length === 10 && lead.market !== 'export' ? `91${digits}` : digits;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
}

const firstName = (lead) => (lead.contactPerson || '').split(/\s+/)[0];
const desk = (lead) => (lead.market === 'export'
  ? { name: 'Export Desk', email: 'export@fabvoguestudio.com' }
  : { name: 'Wholesale Desk', email: 'wholesale@fabvoguestudio.com' });

/** Email templates that fit this lead's stage, best first. */
export function emailTemplates(lead) {
  const name = firstName(lead) || `${lead.brandName} team`;
  const fabrics = (lead.account?.topFabrics?.length ? lead.account.topFabrics : lead.fabrics || []).slice(0, 2).join(' and ');
  const sign = `\n\nWarm regards,\nFAB VOGUE Studio ${desk(lead).name}\n${desk(lead).email}`;
  const isExport = lead.market === 'export';
  const pricing = isExport
    ? 'For export orders we quote per fabric, quantity and destination. Reply with what you are planning and we will send a formal quote.'
    : lead.source === 'zoho_customer'
      ? 'You may also qualify for our wholesale pricing: it applies from 99 meters per order, and you can mix and match fabrics, designs and colours to reach it. Registering takes two minutes at https://www.fabvoguestudio.com/pages/wholesale'
      : 'Wholesale pricing applies from 99 meters per order, and you can mix and match fabrics, designs and colours to reach it. Smaller orders are welcome at our regular prices.';
  const list = [];
  const touch = Number(lead.touchCount) || 0;
  if (lead.account && touch === 0 && lead.account.segment !== 'pending_approval') {
    const recent = ['active', 'cooling'].includes(lead.account.segment);
    const opening = lead.account.segment === 'never_ordered'
      ? 'Your wholesale account with FAB VOGUE Studio is approved, but you have not placed an order yet. Is there anything we can help with: fabric choice, prints or pricing?'
      : recent
        ? `Thank you for your recent orders with FAB VOGUE Studio.${fabrics ? ` We noticed you buy a lot of our ${fabrics}.` : ''} We have just added new digital prints and fabric bases, and wanted you to see them first.`
        : `It has been a while since your last order with us, and we wanted to check in.${fabrics ? ` You last picked our ${fabrics}.` : ''} Since then we have added new digital prints and fabric bases.`;
    list.push({ id: 'winback', label: 'Win-back', subject: `New prints for ${lead.brandName} | FAB VOGUE Studio`,
      body: `Hello ${name},\n\n${opening}\n\n${pricing}\n\nShall I courier you a complimentary swatch pack of the new bases${fabrics ? ` along with fresh ${fabrics} prints` : ''}? Just reply with the address and I will arrange it.${sign}` });
  }
  if (touch > 0 && !lead.lastReplyAt) {
    list.push({ id: 'followup', label: `Follow-up (email ${touch + 1})`, subject: lead.lastEmailSubject || `Fabric sourcing for ${lead.brandName}`,
      body: `Hello ${name},\n\nFollowing up on my note below. If you are planning new prints this season, I would be happy to courier a complimentary swatch pack of our running bases${fabrics ? ` (${fabrics})` : ''} so you can check the hand-feel and print quality.\n\nShall I send one to your studio?${sign}` });
  }
  list.push({ id: 'first', label: 'First touch', subject: `Fabric sourcing for ${lead.brandName} | FAB VOGUE Studio`,
    body: `Hello ${name},\n\nI am reaching out from FAB VOGUE Studio in Surat. We digitally print and dye 52+ running fabric bases for fashion brands and manufacturers.\n\n${pricing}\n\nIf you are reviewing fabric suppliers, please share the fabric bases, approximate quantity and delivery city. I can then prepare a tailored quote and send a complimentary swatch pack.${sign}` });
  if (lead.stage === 'swatch_sent' && lead.awb) {
    list.push({ id: 'dispatched', label: 'Swatch dispatched', subject: `Your swatch pack is on the way | ${lead.courier || 'courier'} ${lead.awb}`,
      body: `Hello ${name},\n\nYour complimentary swatch pack has been dispatched by ${lead.courier || 'courier'}, tracking number ${lead.awb}.\n\nIt holds ${(lead.fabrics || []).join(', ') || 'our running bases'}. When it arrives, feel the drape and hold the prints to daylight; I will call you after delivery to hear what worked.${sign}` });
  }
  if (lead.stage === 'swatch_delivered') {
    list.push({ id: 'delivered', label: 'After delivery', subject: 'Did the swatches work for you?',
      body: `Hello ${name},\n\nThe swatch pack reached you. Which bases felt right for your collection? If you share one or two designs, we can print a 3 to 5 meter trial so you can test fit and cuts before a bulk order.${sign}` });
  }
  if (lead.stage === 'quote_sent') {
    list.push({ id: 'quote', label: 'Quote follow-up', subject: `Your quote | FAB VOGUE Studio`,
      body: `Hello ${name},\n\nChecking in on the quote I shared. Happy to adjust the fabric, quantity or delivery timing to fit your plan. Shall I book production for you?${sign}` });
  }
  return list;
}

/** WhatsApp openers (short, no links to track): the team's field-tested scripts, shortened. */
export function whatsappTemplates(lead) {
  const name = firstName(lead) || lead.brandName;
  return [
    { id: 'wa_first', label: 'First touch', text: `Hello ${name}! This is FAB VOGUE Studio from Surat. We digitally print 52+ running fabric bases (Modal Satin, Crepe, Chiffon, Muslin) for fashion brands, sampling from 3 to 5 m per design with strict design confidentiality. May we courier a complimentary swatch pack to your studio this week?` },
    { id: 'wa_followup', label: 'Nudge', text: `Hello ${name}, just checking if you saw our note about fabric swatches. Happy to send the pack whenever it suits you.` },
    { id: 'wa_address', label: 'Ask address', text: `Hello ${name}, please share your studio address and phone for the swatch pack. We dispatch within 24 hours.` },
  ];
}

// Indicative rates per 1 INR; the quote tool says to confirm before sending.
export const FX = { INR: 1, AED: 0.044, USD: 0.012, EUR: 0.011, GBP: 0.0095 };

export function computeQuote({ fabric, meters, marginPct, currency = 'INR', freightPerKg = 0 }) {
  const qty = Math.max(1, Number(meters) || 0);
  const cost = Number(fabric?.purchasePrice) || 0;
  const kg = Math.max(0.5, ((Number(fabric?.weightInGrams) || 80) * qty) / 1000);
  const exMill = Math.round(cost * (1 + (Number(marginPct) || 0) / 100));
  const freightPerMeter = Math.round((kg * (Number(freightPerKg) || 0)) / qty);
  const rate = FX[currency] || 1;
  const fmt = (inr) => (currency === 'INR' ? Math.round(inr) : Math.round(inr * rate * 100) / 100);
  return {
    exMill: fmt(exMill),
    landed: fmt(exMill + freightPerMeter),
    total: fmt((exMill + freightPerMeter) * qty),
    gstInclusive: currency === 'INR' ? Math.round(exMill * 1.05) : null,
    kg: Math.round(kg * 10) / 10,
  };
}
